import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  modalityTerms,
  templates,
  type ModalityTermRef,
  type ModalityTermState,
  type ModalityTermTag,
  type ModalityTermTagKind,
  type ModalityTermVersion,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Modal } from '@/shared/ui/Modal';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { AVISO_AUTOAPROVACAO, autoriaDe } from './autoaprovacao';
import { campoRecusado, chaveDoTexto, tiposDeTag } from './textos';

/**
 * Grade "TAGs de preenchimento automatico": Label, Tag e "Selecione o tipo", uma
 * linha por TAG. A Tag e a marcacao da plataforma, sem as chaves; a TAG nova comeca
 * pelo grupo do texto (objeto. no objeto, clausula. na condicao particular).
 */
export function GradeDeTags({
  tags,
  onChange,
  grupo,
  disabled,
}: {
  tags: ModalityTermTag[];
  onChange: (tags: ModalityTermTag[]) => void;
  grupo: 'objeto' | 'clausula';
  disabled: boolean;
}) {
  function mudar(indice: number, parte: Partial<ModalityTermTag>) {
    onChange(tags.map((tag, i) => (i === indice ? { ...tag, ...parte } : tag)));
  }
  return (
    <section
      aria-label="TAGs de preenchimento automatico"
      className="flex flex-col gap-3 rounded-md border border-line p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          TAGs de preenchimento automatico
        </h4>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange([
              ...tags,
              { label: '', tag_key: `${grupo}.`, input_kind: 'texto' },
            ])
          }
        >
          Adicionar TAG
        </Button>
      </div>
      {tags.length === 0 ? (
        <p className="text-2xs text-ink-500">
          Nenhuma TAG declarada. A TAG declarada aqui vira um campo a preencher na
          cotacao e pode ser incluida no texto.
        </p>
      ) : null}
      {tags.map((tag, indice) => (
        <div
          key={indice}
          className="grid items-end gap-3 border-t border-line pt-3 first:border-t-0 first:pt-0 sm:grid-cols-[1fr_1fr_1fr_auto]"
        >
          <Field
            label="Label"
            value={tag.label}
            maxLength={120}
            disabled={disabled}
            onChange={(e) => mudar(indice, { label: e.target.value })}
          />
          <Field
            label="Tag"
            value={tag.tag_key}
            maxLength={80}
            placeholder={`${grupo}.numero_contrato`}
            disabled={disabled}
            onChange={(e) => mudar(indice, { tag_key: e.target.value })}
          />
          <Select
            label="Selecione o tipo"
            options={tiposDeTag}
            value={tag.input_kind}
            disabled={disabled}
            onChange={(e) =>
              mudar(indice, { input_kind: e.target.value as ModalityTermTagKind })
            }
          />
          <AcaoComIcone
            acao="remover"
            rotulo="Remover"
            disabled={disabled}
            aria-label={`Remover a TAG ${tag.label || indice + 1}`}
            onClick={() => onChange(tags.filter((_, i) => i !== indice))}
          />
        </div>
      ))}
    </section>
  );
}

/**
 * "Incluir TAG": poe a marcacao no cursor do editor. As opcoes sao as TAGs declaradas
 * no texto e o vocabulario dos textos da modalidade
 * (`GET /api/v1/template-variables?context=objeto`). Sem acesso ao vocabulario,
 * ficam so as declaradas.
 */
export function IncluirTag({
  declaradas,
  onIncluir,
  disabled,
}: {
  declaradas: ModalityTermTag[];
  onIncluir: (marcacao: string) => void;
  disabled: boolean;
}) {
  const vocabulario = useQuery({
    queryKey: ['template-variables', 'objeto'],
    queryFn: ({ signal }) => templates.variables('objeto', signal),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const vistas = new Set<string>();
  const opcoes: { value: string; label: string }[] = [];
  for (const tag of declaradas) {
    if (!/^[a-z_]+\.[a-z_]+$/.test(tag.tag_key) || vistas.has(tag.tag_key)) continue;
    vistas.add(tag.tag_key);
    opcoes.push({
      value: tag.tag_key,
      label: `${tag.label || tag.tag_key} (TAG do texto)`,
    });
  }
  for (const variavel of vocabulario.data ?? []) {
    if (variavel.list || vistas.has(variavel.name)) continue;
    vistas.add(variavel.name);
    opcoes.push({
      value: variavel.name,
      label: `${variavel.description} (${variavel.name})`,
    });
  }
  return (
    <Select
      label="Incluir TAG"
      options={opcoes}
      placeholder="Escolha a TAG para incluir no cursor"
      value=""
      disabled={disabled || opcoes.length === 0}
      hint={
        vocabulario.isError
          ? 'O vocabulario da plataforma nao abriu; ficam as TAGs declaradas no texto.'
          : undefined
      }
      onChange={(e) => {
        if (e.target.value) onIncluir(`{{${e.target.value}}}`);
      }}
    />
  );
}

function SituacaoDoTexto({
  estado,
  podeAtivar,
  onAtivar,
}: {
  estado: ModalityTermState;
  podeAtivar: boolean;
  onAtivar: () => void;
}) {
  const { active: ativa, draft: rascunho } = estado;
  const autoria = autoriaDe(rascunho?.authored_by_you, rascunho?.may_self_approve);
  return (
    <Alert tone="info" title="Texto versionado">
      <div className="flex flex-col gap-2">
        <p>
          Salvar grava o rascunho; outra pessoa, com a alcada, poe a versao em vigor com
          justificativa. A cotacao copia o texto em vigor, e a que ja copiou continua
          com a versao dela.
        </p>
        <p>
          {ativa
            ? `Em vigor: versao ${ativa.version}, ativada por ${ativa.activated_label || 'nao informado'} em ${formatDateTime(ativa.activated_at)}.`
            : 'Nenhuma versao em vigor.'}{' '}
          {rascunho
            ? `Rascunho aberto: versao ${rascunho.version}, por ${rascunho.created_label || 'carga do sistema'}.`
            : 'Salvar abre um rascunho com o texto da tela.'}
        </p>
        {rascunho && podeAtivar ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={onAtivar} disabled={autoria.barrada}>
              Ativar versao {rascunho.version}
            </Button>
            {autoria.barrada ? (
              <span className="text-2xs">
                Voce escreveu ou editou este rascunho. Outra pessoa com a alcada ativa a
                versao.
              </span>
            ) : null}
            {autoria.autoaprovacao ? (
              <span className="text-2xs">{AVISO_AUTOAPROVACAO}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Alert>
  );
}

function AtivarTexto({
  referencia,
  rascunho,
  onFechar,
  onAtivado,
}: {
  referencia: ModalityTermRef;
  rascunho: ModalityTermVersion;
  onFechar: () => void;
  onAtivado: () => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const ativar = useMutation({
    mutationFn: () =>
      modalityTerms.activateTerm(referencia, rascunho.id, justificativa),
    onSuccess: onAtivado,
  });
  const apiError = ativar.error instanceof ApiError ? ativar.error : null;
  return (
    <Modal
      open
      onClose={onFechar}
      title={`Ativar versao ${rascunho.version}`}
      description="A versao em vigor passa a revogada, e esta entra em vigor a partir de agora."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          ativar.mutate();
        }}
      >
        {ativar.error && !fieldErrorOf(apiError, 'justification') ? (
          <Alert tone="danger" title="Nao foi possivel ativar">
            {ativar.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Justificativa"
          required
          value={justificativa}
          error={fieldErrorOf(apiError, 'justification')}
          onChange={(e) => setJustificativa(e.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button
            type="submit"
            loading={ativar.isPending}
            disabled={justificativa.trim() === ''}
          >
            Ativar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function VersoesDoTexto({ referencia }: { referencia: ModalityTermRef }) {
  const consulta = useQuery({
    queryKey: [...chaveDoTexto(referencia), 'versions'],
    queryFn: ({ signal }) => modalityTerms.listTermVersions(referencia, signal),
  });
  return (
    <DetailCard
      title="Versoes do texto"
      description="Toda versao fica guardada: o rascunho, a ativa e as revogadas."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'versao',
            header: 'Versao',
            render: (v: ModalityTermVersion) => `Versao ${v.version}`,
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (v: ModalityTermVersion) => (
              <StatusBadge kind="versao" value={v.status} />
            ),
          },
          {
            key: 'caracteres',
            header: 'Caracteres',
            align: 'right',
            render: (v: ModalityTermVersion) => v.characters.toLocaleString('pt-BR'),
          },
          {
            key: 'aberta',
            header: 'Aberta por',
            render: (v: ModalityTermVersion) => v.created_label || 'carga do sistema',
          },
          {
            key: 'ativada',
            header: 'Ativada por',
            render: (v: ModalityTermVersion) =>
              v.activated_label
                ? `${v.activated_label} em ${formatDateTime(v.activated_at)}`
                : '-',
          },
          {
            key: 'motivo',
            header: 'Justificativa',
            render: (v: ModalityTermVersion) =>
              [v.justification, v.revocation_reason].filter(Boolean).join(' . ') || '-',
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(v) => v.id}
        empty={<EmptyState icon="layers" title="Nenhuma versao do texto ainda" />}
      />
    </DetailCard>
  );
}

interface FormularioProps {
  referencia: ModalityTermRef;
  estado: ModalityTermState;
  rotuloDoEditor: string;
  ajudaDoEditor: string;
  rotuloDeSalvar: string;
  comTags: boolean;
}

function FormularioDoTexto({
  referencia,
  estado,
  rotuloDoEditor,
  ajudaDoEditor,
  rotuloDeSalvar,
  comTags,
}: FormularioProps) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const editor = useRef<RichTextEditorHandle>(null);
  const idDaCotacao = useId();

  const base = estado.draft ?? estado.active;
  const podeEscrever = can('parametrizacao.texto.update');
  const podeAtivar = can('parametrizacao.texto.approve');

  const [referenciaDaSeguradora, setReferencia] = useState(
    base?.insurer_reference ?? '',
  );
  const [corpo, setCorpo] = useState(base?.body ?? '');
  const [tags, setTags] = useState<ModalityTermTag[]>(base?.tags ?? []);
  const [naCotacao, setNaCotacao] = useState(base?.enabled_on_quote ?? true);
  const [ativando, setAtivando] = useState(false);

  function atualizar() {
    void queryClient.invalidateQueries({ queryKey: chaveDoTexto(referencia) });
  }

  const salvar = useMutation({
    mutationFn: () =>
      modalityTerms.saveTerm(referencia, {
        insurer_reference: referenciaDaSeguradora.trim(),
        body: corpo,
        enabled_on_quote: naCotacao,
        ...(comTags ? { tags } : {}),
      }),
    onSuccess: (versao) => {
      toast.success(`Rascunho ${versao.version} salvo.`);
      atualizar();
    },
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const erroDoCorpo = fieldErrorOf(apiError, 'body');
  const erroDasTags = campoRecusado(apiError).startsWith('tags')
    ? apiError?.message
    : undefined;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  const desligado = !podeEscrever;
  return (
    <div className="flex flex-col gap-5">
      <SituacaoDoTexto
        estado={estado}
        podeAtivar={podeAtivar}
        onAtivar={() => setAtivando(true)}
      />

      <form className="flex flex-col gap-4" onSubmit={enviar} noValidate>
        {salvar.error && !erroDoCorpo && !erroDasTags ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Field
          label="Referencia da Seguradora"
          value={referenciaDaSeguradora}
          maxLength={120}
          placeholder="Digite a referencia da seguradora"
          disabled={desligado}
          error={fieldErrorOf(apiError, 'insurer_reference')}
          onChange={(e) => setReferencia(e.target.value)}
        />

        {comTags ? (
          <>
            <GradeDeTags
              tags={tags}
              onChange={setTags}
              grupo="objeto"
              disabled={desligado}
            />
            {erroDasTags ? (
              <p className="text-2xs font-medium text-danger-700">{erroDasTags}</p>
            ) : null}
          </>
        ) : null}

        <IncluirTag
          declaradas={comTags ? tags : []}
          disabled={desligado}
          onIncluir={(marcacao) => editor.current?.inserirTexto(marcacao)}
        />

        <RichTextEditor
          label={rotuloDoEditor}
          value={corpo}
          onChange={setCorpo}
          maxLength={estado.max_characters}
          disabled={desligado}
          error={erroDoCorpo}
          help={ajudaDoEditor}
          editorRef={editor}
          rows={12}
        />

        <div className="flex items-center gap-2">
          <input
            id={idDaCotacao}
            type="checkbox"
            checked={naCotacao}
            disabled={desligado}
            onChange={(e) => setNaCotacao(e.target.checked)}
            className="size-4 accent-[var(--color-brand-500)]"
          />
          <FieldLabel
            htmlFor={idDaCotacao}
            label="Habilitar na cotacao"
            className="text-sm text-ink-800"
          />
        </div>

        {podeEscrever ? (
          <div className="flex justify-end">
            <Button type="submit" loading={salvar.isPending}>
              {rotuloDeSalvar}
            </Button>
          </div>
        ) : null}
      </form>

      <VersoesDoTexto referencia={referencia} />

      {ativando && estado.draft ? (
        <AtivarTexto
          referencia={referencia}
          rascunho={estado.draft}
          onFechar={() => setAtivando(false)}
          onAtivado={() => {
            setAtivando(false);
            toast.success('Versao ativada.');
            atualizar();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Aba de texto versionado da modalidade (Objeto, Condicao Geral, Condicoes
 * Especiais, Criterios para Enquadramento de Riscos) e os textos da cobertura
 * adicional: "Referencia da Seguradora", o editor com "Incluir TAG", a grade de TAGs
 * no objeto, "Habilitar na cotacao" e o botao de salvar com o nome do OnPoint.
 *
 * O HTML e saneado no navegador e de novo no servidor, que confere as marcacoes.
 */
export function TextoDaModalidade({
  referencia,
  rotuloDoEditor,
  ajudaDoEditor,
  rotuloDeSalvar,
  comTags = false,
}: {
  referencia: ModalityTermRef;
  rotuloDoEditor: string;
  ajudaDoEditor: string;
  rotuloDeSalvar: string;
  comTags?: boolean;
}) {
  const consulta = useQuery({
    queryKey: chaveDoTexto(referencia),
    queryFn: ({ signal }) => modalityTerms.getTerm(referencia, signal),
  });
  if (consulta.isPending || consulta.isError) {
    return (
      <DetailCard
        title={rotuloDoEditor}
        loading={consulta.isPending}
        error={consulta.error}
      >
        {null}
      </DetailCard>
    );
  }
  const estado = consulta.data;
  const base = estado.draft ?? estado.active;
  return (
    // A chave remonta o formulario quando a versao muda, para que os campos partam do
    // que esta gravado depois de salvar ou ativar.
    <FormularioDoTexto
      key={`${base?.id ?? 'novo'}-${base?.updated_at ?? ''}`}
      referencia={referencia}
      estado={estado}
      rotuloDoEditor={rotuloDoEditor}
      ajudaDoEditor={ajudaDoEditor}
      rotuloDeSalvar={rotuloDeSalvar}
      comTags={comTags}
    />
  );
}
